import { BillingCategoryCode } from './Receipt'
import Tenant, { TenantData } from './Tenant'

///////////////////////////////////
// Type
///////////////////////////////////

export const EmailType = {
  PC: 0,
  Mobile: 1,
}
export type EmailType = (typeof EmailType)[keyof typeof EmailType]

export const MeterType = {
  SmartMeter: 0,
  Analog: 1,
} as const
export type MeterType = (typeof MeterType)[keyof typeof MeterType]

///////////////////////////////////
// UserCore
///////////////////////////////////

export type UserCoreData = {
  id: number
  code: string
  firstName: string | null
  lastName: string
  firstNameKana: string | null
  lastNameKana: string
  phoneNumber1: string | null
  email: string
  emailType: EmailType
}

export class UserCore {
  declare id: number
  declare code: string
  declare firstName: string | null
  declare lastName: string
  declare firstNameKana: string | null
  declare lastNameKana: string
  declare phoneNumber1: string | null
  declare email: string
  declare emailType: EmailType

  // Computed properties ------------------------------------------------------

  get codeForDisplay() {
    return this.code.slice(0, 8)
  }

  get name() {
    if (!this.firstName) return this.lastName
    return `${this.lastName} ${this.firstName}`
  }

  get kana() {
    if (!this.firstNameKana) return this.lastNameKana
    return `${this.lastNameKana} ${this.firstNameKana}`
  }

  // Constructor --------------------------------------------------------------

  constructor(params: UserCoreData) {
    Object.assign(this, params)
  }
}

///////////////////////////////////
// User
///////////////////////////////////

export type UserData = UserCoreData & {
  roomTypeName: string | null
  hasGasMeter: boolean
  meterType: MeterType
  paymentMethods: (typeof BillingCategoryCode.CreditCard | typeof BillingCategoryCode.AccountTransfer)[]

  evUser: UserCoreData | null
  tenant: TenantData
}

export default class User extends UserCore {
  declare roomTypeName: string | null
  declare hasGasMeter: boolean
  declare meterType: MeterType
  declare paymentMethods: (typeof BillingCategoryCode.CreditCard | typeof BillingCategoryCode.AccountTransfer)[]

  declare tenant: Tenant
  declare evUser: UserCore | null

  // Computed properties ------------------------------------------------------

  get displayName() {
    if (this.tenant.billingCategory.code !== BillingCategoryCode.AccountTransfer) return this.name
    return this.tenant.payerName || this.name
  }

  get building() {
    return this.tenant.room.building
  }

  get buildingName() {
    return this.building.name
  }

  get roomName() {
    return this.tenant.room.name
  }

  get isSmartMeter() {
    return this.meterType === MeterType.SmartMeter
  }

  get hasEVCharger() {
    return !!this.evUser
  }

  // Constructor --------------------------------------------------------------

  constructor(params: UserData) {
    delete params['hasEVCharger'] // 移行用
    super(params)

    this.roomTypeName = params.roomTypeName
    this.hasGasMeter = params.hasGasMeter
    this.meterType = params.meterType
    this.paymentMethods = params.paymentMethods

    this.tenant = new Tenant(params.tenant)
    this.evUser = params.evUser ? new UserCore(params.evUser) : null
  }
}
